import stripAnsi from "strip-ansi";

export function stripAnsiFromTerminalOutput(str: string): string {
  const result = stripAnsi(str)
    .replace(/\\(?!\w)/g, "")
    .replace(/^(\/\-)/g, "")
    // Remove any remaining | or \ at start of lines
    .replace(/^[|\\-]/gm, "")
    // Remove backslashes at start of lines
    .replace(/^\\/gm, "")
    .replace(/^(\/\-)/g, "");

  return result
    .replace(/\u001b\[\d+(?:;\d+)*m/g, "")
    .replace(/\u001b\[0m/g, "")
    .replace(/[\u2500-\u257F]/g, "")
    .replace(/^[╭╰]─+/gm, "")
    .trim();
}

export const detectViteErrors = (terminalOutput: string) => {
  // Helper function to extract file information
  function extractFileInfo(str) {
    const fileMatch = str.match(/(?:File: )?([^:]+):(\d+):(\d+)/);
    if (fileMatch) {
      return {
        filePath: fileMatch[1],
        line: parseInt(fileMatch[2]),
        column: parseInt(fileMatch[3]),
      };
    }
    return null;
  }

  // Helper function to extract code snippet
  function extractCodeSnippet(str) {
    const lines = str.split("\n");
    const codeLines = [];

    for (const line of lines) {
      const lineMatch = line.match(/│\s*(.+)$/);
      if (lineMatch) {
        // @ts-expect-error TS2345
        codeLines.push(lineMatch[1].trim());
      }
    }

    return codeLines;
  }

  // Split terminal output into individual error blocks
  function splitErrorBlocks(output) {
    // Pattern to identify the start of an error block
    const errorStarts = [
      // Existing patterns
      /Error:/i, // Standard error prefix
      /Error /i, // Standard error prefix
      /Failed to load config/i, // Config errors
      /Failed to compile/i, // Build errors
      /\[vite\].*error/i, // Vite errors
      /TypeError:/i, // Type errors
      /SyntaxError:/i, // Syntax errors
      /ReferenceError:/i, // Reference errors
      /Module not found/i, // Module errors
      /Cannot find module/i, // Import errors
      /Unable to resolve/i, // Module resolution errors
      /Web Bundling failed/i, // Bundling errors

      // Add general npm error pattern
      /npm.*error/i, // General npm errors

      // Next.js specific patterns
      /\[next\].*error/i, // Next.js errors
      /Page.*error/i, // Page-related errors
      /Invalid.*configuration/i, // Configuration errors
      /404.*not found/i, // 404 errors
      /API.*error/i, // API route errors
      /getServerSideProps.*failed/i, // Server-side props errors
      /getStaticProps.*failed/i, // Static props errors
      /getStaticPaths.*failed/i, // Static paths errors
      /Middleware.*error/i, // Middleware errors
      /Build.*error/i, // Build-specific errors
      /\[webpack\].*error/i, // Webpack errors
      /Server Error/i, // Generic server errors
      /Invalid route/i, // Routing errors
      /Invalid.*component/i, // Component-related errors
    ];

    let blocks: string[] = [];
    let currentBlock = "";

    const lines = output.split("\n");
    let isInBlock = false;

    for (const line of lines) {
      // Check if this line starts a new error
      const isErrorStart = errorStarts.some((pattern) => pattern.test(line));

      // Skip HMR updates, build success messages, tempobook errors and other non-error messages
      if (
        line.includes("hmr update") ||
        line.includes("hmr invalidate") || // Skip HMR invalidate messages
        line.includes("Could not Fast Refresh") || // Skip Fast Refresh errors
        line.includes("Unauthorized request from https://us.posthog.com") || // Skip Posthog errors
        line.includes("[vite] connected") ||
        line.match(/^\d{2}:\d{2}:\d{2}/) || // Skip timestamp lines
        line.includes("build-no-errors") ||
        (line.includes("Pre-transform error") && line.includes("tempobook/")) || // Skip tempobook errors
        line.includes(
          "rm: cannot remove 'node_modules': Device or resource busy"
        ) // Skip node_modules removal messages
      ) {
        continue;
      }

      if (isErrorStart) {
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        currentBlock = line;
        isInBlock = true;
      } else if (isInBlock && line.trim()) {
        currentBlock += "\n" + line;
      }
    }

    if (currentBlock) {
      blocks.push(currentBlock);
    }

    return blocks;
  }

  // Analyze individual error block
  function analyzeError(errorBlock) {
    const cleanBlock = stripAnsiFromTerminalOutput(errorBlock);
    const location = extractFileInfo(cleanBlock);

    // Error patterns with their corresponding suggestions
    const errorPatterns = {
      missingClosingTag: {
        pattern: /(?:Expected corresponding JSX closing tag|Unclosed tag)/i,
        suggestion: "Check for missing closing tags in your JSX elements",
      },
      unexpectedToken: {
        pattern: /Unexpected token/i,
        suggestion:
          "Look for syntax errors like missing brackets or semicolons",
      },
      syntaxError: {
        pattern: /Syntax Error/i,
        suggestion:
          "Verify your code syntax, particularly around the highlighted area",
      },
      missingImport: {
        pattern: /Cannot find module|Failed to resolve/i,
        suggestion:
          "Ensure all imported modules exist and are correctly specified",
      },
      moduleResolutionError: {
        pattern: /Unable to resolve.*from/i,
        suggestion:
          "Check if the imported module exists at the specified path and verify your import paths",
      },
      webBundlingError: {
        pattern: /Web Bundling failed/i,
        suggestion:
          "Examine bundler configuration and check for errors in imported modules",
      },
      invalidJSX: {
        pattern: /Invalid JSX|JSX expression/i,
        suggestion: "Verify JSX syntax rules are followed",
      },
      cssError: {
        pattern: /Invalid CSS|Failed to parse CSS/i,
        suggestion: "Check CSS syntax and class names for errors",
      },
      typeError: {
        pattern: /Type.*error|TS\d+/i,
        suggestion: "Verify type definitions and usage",
      },
      buildError: {
        pattern: /Failed to compile|Build failed/i,
        suggestion: "Check build configuration and dependencies",
      },
      runtimeError: {
        pattern: /Cannot read proper(?:ty|ties)|is not (?:defined|a function)/i,
        suggestion: "Verify object existence before accessing properties",
      },
      nextConfigError: {
        pattern: /Invalid next\.config\.js/i,
        suggestion:
          "Check your next.config.js file for syntax errors or invalid configuration options",
      },
      pageNotFound: {
        pattern: /404.*page could not be found/i,
        suggestion:
          "Verify the page exists in the pages directory and the route is correct",
      },
      apiRouteError: {
        pattern:
          /API resolved without sending a response|API Routes must return a response/i,
        suggestion:
          "Ensure your API route is returning a response using res.status().json() or similar",
      },
      getServerSidePropsError: {
        pattern:
          /Error occurred in getServerSideProps|getServerSideProps failed/i,
        suggestion:
          "Check your getServerSideProps function for errors in data fetching or processing",
      },
      getStaticPropsError: {
        pattern: /Error occurred in getStaticProps|getStaticProps failed/i,
        suggestion: "Verify your getStaticProps function for build-time errors",
      },
      getStaticPathsError: {
        pattern: /Error occurred in getStaticPaths|getStaticPaths failed/i,
        suggestion:
          "Check your getStaticPaths function for errors in path generation",
      },
      middlewareError: {
        pattern: /Middleware.*error|Error in middleware/i,
        suggestion:
          "Review your middleware implementation for errors or invalid configurations",
      },
      routingError: {
        pattern: /Failed to compile.*pages\/|Multiple page files found/i,
        suggestion: "Check your page routing structure and file naming",
      },
      imageOptimization: {
        pattern: /Failed to optimize image|Invalid Image configuration/i,
        suggestion: "Verify Image component props and next/image configuration",
      },
      fontOptimization: {
        pattern: /Failed to optimize fonts|Invalid font configuration/i,
        suggestion:
          "Check font configuration in next.config.js and font loading implementation",
      },
      serverComponentError: {
        pattern:
          /'use client'.*server component|Cannot use.*in server component/i,
        suggestion:
          "Ensure client-side code is not used in Server Components and vice versa",
      },
      clientComponentError: {
        pattern:
          /'use server'.*client component|Cannot use.*in client component/i,
        suggestion:
          "Check for incorrect usage of Server Actions or server-only code in Client Components",
      },
      appDirError: {
        pattern: /app directory.*error|Invalid app directory structure/i,
        suggestion:
          "Verify your app directory structure follows Next.js conventions",
      },
      layoutError: {
        pattern: /Error in layout\.js|Invalid layout configuration/i,
        suggestion:
          "Check your layout files for errors or invalid configurations",
      },
      metadataError: {
        pattern: /Invalid metadata|Metadata configuration error/i,
        suggestion:
          "Verify your metadata configuration in layout.js or page.js files",
      },
      internationalizationError: {
        pattern: /i18n.*configuration|Invalid locale/i,
        suggestion:
          "Check your internationalization configuration and locale implementations",
      },

      // Add general npm error pattern
      npmError: {
        pattern: /npm.*error/i,
        suggestion:
          "This is an npm error. Try removing node_modules folder and package-lock.json file, then run npm install again. Do not delete the npm cache ever.",
      },
    };

    // Determine error type and suggestion
    let errorType = "unknown";
    let suggestion = "Review the code around this area";

    for (const [
      type,
      { pattern, suggestion: typeSuggestion },
    ] of Object.entries(errorPatterns)) {
      if (pattern.test(cleanBlock)) {
        errorType = type;
        suggestion = typeSuggestion;
        break;
      }
    }

    // Convert to DevErrorJson format while preserving type, message and suggestion
    return {
      type: errorType, // Keep error type for grouping
      message: cleanBlock.split("\n")[0].trim(), // First line as main message
      filename: location?.filePath || "",
      stack: cleanBlock, // Full error message as stack
      storyboards: [], // Vite errors don't typically specify storyboards
      count: 1,
      lineno: location?.line,
      colno: location?.column,
      suggestion: suggestion, // Keep the suggestion field
    };
  }

  // Process all errors in the output
  const errorBlocks = splitErrorBlocks(terminalOutput);
  const result: any = {
    hasError: errorBlocks.length > 0,
    errorCount: errorBlocks.length,
    errors: errorBlocks.map((block) => analyzeError(block)),
    summary: {
      totalErrors: 0,
      byType: {},
      byFile: {},
      byTypeAndMessage: {},
    },
  };

  // Generate summary statistics
  result.errors.forEach((error) => {
    result.summary.totalErrors++;

    // Count by error type
    result.summary.byType[error.type] =
      (result.summary.byType[error.type] || 0) + 1;

    // Count by file
    if (error.location?.filePath) {
      result.summary.byFile[error.location.filePath] =
        (result.summary.byFile[error.location.filePath] || 0) + 1;
    }

    // Group by type and message
    if (!result.summary.byTypeAndMessage[error.type]) {
      result.summary.byTypeAndMessage[error.type] = {};
    }
    const cleanMessage = error.message.split("\n")[0].trim(); // Take first line of message
    if (!result.summary.byTypeAndMessage[error.type][cleanMessage]) {
      result.summary.byTypeAndMessage[error.type][cleanMessage] = [];
    }
    result.summary.byTypeAndMessage[error.type][cleanMessage].push(error);
  });

  // Add helper methods
  result.getErrorsByType = function (type) {
    return this.errors.filter((error) => error.type === type);
  };

  result.getErrorsByFile = function (filePath) {
    return this.errors.filter((error) => error.location?.filePath === filePath);
  };

  result.getMostCommonErrorType = function () {
    return (
      Object.entries(this.summary.byType).sort(
        ([, a]: any, [, b]: any) => b - a
      )[0]?.[0] || null
    );
  };

  result.getErrorsByTypeAndMessage = function (type, message) {
    return this.summary.byTypeAndMessage[type]?.[message] || [];
  };

  result.getGroupedErrors = function () {
    return this.summary.byTypeAndMessage;
  };

  return result;
};
